import { Draggable } from "@msuite/ui/dnd";
import { Icon } from "@msuite/ui/icons";
import { cva, type VariantProps } from "@msuite/ui/libs/cva";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardNumber } from "@msuite/utils/numbers";
import { guardString } from "@msuite/utils/strings";
import type { Vehicle } from "@msuite/utils/types";
import { client } from "@/hono";

/** Props Interface */
interface VehicleCardWithIdProps extends VariantProps<typeof vehicleCard> {
	vehicleId: string;
	vehicle?: never;
	withDetails?: boolean;
	isDraggable?: boolean;
}

interface VehicleCardWithVehicleProps extends VariantProps<typeof vehicleCard> {
	vehicleId?: string;
	vehicle: Vehicle;
	withDetails?: boolean;
	isDraggable?: boolean;
}

type VehicleCardProps = VehicleCardWithIdProps | VehicleCardWithVehicleProps;

export function VehicleCard({
	vehicleId,
	vehicle: _vehicle,
	withDetails = true,
	variant,
	isDraggable = true,
}: VehicleCardProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Classes */
	const classes = vehicleCard({ variant });

	/** Query */
	const { data: vehicle } = useQuery({
		queryKey: ["vehicle", vehicleId || ""],
		queryFn: async () => {
			if (_vehicle) return _vehicle;
			if (!vehicleId) throw new Error("Vehicle ID is required");
			const response = await client.vehicles[":vehicleId"].$get({ param: { vehicleId } });
			const data = await responseHandler(response);
			return data.vehicle;
		},
	});

	if (!vehicle) return null;

	/** Render */
	return (
		<Draggable
			type="vehicle"
			item={vehicle}
			className={classes}
			isDisabled={!isDraggable}
		>
			<div className="h-stack justify-between">
				<div className="h-stack">
					<p className="font-semibold">{guardString(vehicle?.licensePlate)}</p>
					{vehicle?._integrations?.length ? (
						<Icon
							color="var(--color-accent-500)"
							name="Link"
						/>
					) : null}
				</div>
				<p>{t(`vehicles.types.${vehicle.type}`)}</p>
			</div>
			{withDetails && (
				<div className="h-stack">
					<p className="label">
						<Icon
							name="Weight"
							className="inline"
						/>{" "}
						{guardNumber(vehicle?.maximumLoadKg)} kg{" / "}
						<Icon
							name="RulerDimensionLine"
							className="inline"
						/>{" "}
						{guardNumber(vehicle?.flatbedDepth)} x {guardNumber(vehicle?.flatbedWidth)} m
					</p>
				</div>
			)}
		</Draggable>
	);
}

const vehicleCard = cva([], {
	variants: {
		variant: {
			default: "card",
			plain: "",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});
